import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/Button";
import Alert from "../components/ui/Alert";
import { Input, PasswordInput } from "../components/ui/Input";
import AppBanner from "../components/AppBanner";
import { apiService } from "../utils/apiService";
import { useLoadingState } from "../hooks/useLoadingState";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const message = location.state?.message;
  const { loading, withLoading } = useLoadingState();

  // Check for session expired message from localStorage
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");

  useEffect(() => {
    const expiredMessage = localStorage.getItem("sessionExpiredMessage");
    if (expiredMessage) {
      setSessionExpiredMessage(expiredMessage);
      localStorage.removeItem("sessionExpiredMessage");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const response = await withLoading(async () => {
        return await apiService.post(
          "/users/login",
          {
            email,
            password,
          },
          { defaultErrorMessage: "Login failed" },
        );
      });

      login(response.user, response.token);
      navigate("/");
    } catch (err) {
      apiService.handleError(err, setError, "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AppBanner />
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-extrabold text-gray-900">
              Welcome back!
            </h1>
            <p className="text-center text-sm text-gray-600 mt-2">
              Sign in to NFL Pick&apos;Em
            </p>
          </div>

          <div className="bg-white shadow-md rounded-lg p-8">
            {message && (
              <Alert color="green" className="mb-4">
                {message}
              </Alert>
            )}
            {sessionExpiredMessage && (
              <Alert color="yellow" className="mb-4">
                {sessionExpiredMessage}
              </Alert>
            )}
            {error && (
              <Alert color="red" className="mb-4">
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button fullWidth type="submit" loading={loading}>
                Sign in
              </Button>
            </form>

            <div className="text-center mt-6">
              <p className="text-sm text-gray-600">
                Don&apos;t have an account?{" "}
                <Link
                  to="/register"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Register
                </Link>
              </p>
              <p className="text-center mt-2">
                <Link
                  to="/reset-password"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Forgot password?
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
